import { useCallback, useEffect, useState } from 'react';

type RouteLike = { params?: Record<string, string> };

type UseTabsOptions<T extends string> = {
  route: RouteLike;
  defaultTab: T;
  paramKey?: string;
  isValidTab: (v: unknown) => v is T;
  isTabAvailable?: (tab: T) => boolean;
  onNavigate: (tab: T) => void;
};

/**
 * Generic hook for tab state synced with a URL param.
 * Use for any page with tabs that should reflect in the URL (?tab= or custom param).
 *
 * @example
 * // Course landing with its own tab enum
 * const { activeTabKey, handleTabClick } = useTabs<CourseLandingTab>({
 *   route,
 *   defaultTab: CourseLandingTab.Overview,
 *   isValidTab: (v): v is CourseLandingTab =>
 *     Object.values(CourseLandingTab).includes(v as CourseLandingTab),
 *   onNavigate: (tab) => routerStore.navigate('courses:overview', { course, tab }),
 * });
 */
export default function useTabs<T extends string>({
  route,
  defaultTab,
  paramKey = 'tab',
  isValidTab,
  isTabAvailable,
  onNavigate,
}: UseTabsOptions<T>) {
  const [activeTabKey, setActiveTabKey] = useState<T>(defaultTab);

  useEffect(() => {
    const tabFromUrl = route?.params?.[paramKey] as T | undefined;

    if (!tabFromUrl || !isValidTab(tabFromUrl)) {
      setActiveTabKey((prev) => (prev === defaultTab ? prev : defaultTab));
      return;
    }
    if (isTabAvailable ? isTabAvailable(tabFromUrl) : true) {
      setActiveTabKey((prev) => (prev === tabFromUrl ? prev : tabFromUrl));
    } else {
      setActiveTabKey((prev) => (prev === defaultTab ? prev : defaultTab));
    }
  }, [route, paramKey, defaultTab, isValidTab, isTabAvailable]);

  const handleTabClick = useCallback(
    (_event: React.MouseEvent, tabIndex: string | number) => {
      const tab = tabIndex as T;
      if (activeTabKey === tab) return;
      setActiveTabKey(tab);
      onNavigate(tab);
    },
    [activeTabKey, onNavigate],
  );

  return { activeTabKey, handleTabClick };
}
